import { useQuery } from '@apollo/client'
import { Stack } from '@mui/material'
import { RosterStatus } from '__generated__/graphql'
import ClientIdentityProvider from 'client/contexts/ClientIdentityContext'
import RosterTableProvider from 'client/contexts/RosterTableContext'
import { GetRosterForClient } from 'client/graphql/search.gql'
import { useIdentity } from 'client/hooks'
import { SearchCandidateListRouteParams } from 'client/routes'
import { clientRouteConfig } from 'client/shared/routeTargets'
import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import MappingHeader from 'shared/components/MappingHeader'
import useToast from 'shared/components/Toast/useToast'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { getPxAsRem } from 'shared/utils'
import CandidatesMappingTable from './CandidatesMappingTable'

export interface RosterMappingCandidateProps {}

const RosterMappingCandidateList: React.FC<
    RosterMappingCandidateProps
> = () => {
    const navigate = useNavigate()
    const toast = useToast()
    const currentRosterId =
        useParams<SearchCandidateListRouteParams>().rosterId ?? ''
    const identity = useIdentity()
    const [canViewRoster, setCanViewRoster] = useState(false)
    const { data: rosterData, loading: rosterLoad } = useQuery(
        GetRosterForClient,
        {
            variables: { rosterId: currentRosterId ?? '' },
            skip: !currentRosterId,
            onError: () => {
                toast({
                    title: 'Error',
                    message: 'Unable to access search, please try again',
                    severity: 'error',
                })
                navigate(`${clientRouteConfig.searches.path}`)
            },
        }
    )

    const rosterName = rosterData?.getRosterForClient?.rosterName
    usePageTitle(rosterName ? `${rosterName} · Mapping` : undefined)

    useCallback(() => {
        const roster = rosterData?.getRosterForClient
        const currentStatus = roster?.status ?? RosterStatus.Draft
        const clientHasAccess =
            (roster?.sharedClients ?? []).filter(
                (sharedClient) =>
                    sharedClient?.client?.id &&
                    sharedClient.client.id === identity?.client?.id
            ).length > 0

        const hasPageAccess =
            clientHasAccess &&
            (currentStatus === RosterStatus.Active ||
                currentStatus === RosterStatus.Closed)
        setCanViewRoster(hasPageAccess)
    }, [identity, rosterData])

    useCallback(() => {
        if (!canViewRoster) {
            toast({
                title: 'Error',
                message: 'Unable to access search, please try again',
                severity: 'error',
            })
            navigate(`${clientRouteConfig.searches.path}`)
        }
    }, [navigate, toast, canViewRoster])

    return (
        <ClientIdentityProvider>
            <RosterTableProvider>
                <Stack
                    display={'flex'}
                    width={'100%'}
                    minWidth={getPxAsRem(375)}
                    padding={getPxAsRem(24, 24)}
                    gap={getPxAsRem(3, 24)}
                    spacing={3}
                    direction="column"
                    flex={1}
                    height={'100%'}
                    alignItems={'flex-start'}
                    overflow={'hidden'}
                >
                    {rosterLoad ? (
                        <LoadingPanel />
                    ) : (
                        <>
                            <MappingHeader
                                showPermissionsIndicator={false}
                                rosterName={
                                    rosterData?.getRosterForClient.rosterName ??
                                    ''
                                }
                                subtitle={
                                    rosterData?.getRosterForClient.company
                                        ?.value ?? ''
                                }
                                showTypePill={false}
                            />
                            <CandidatesMappingTable
                                rosterId={currentRosterId}
                            />
                        </>
                    )}
                </Stack>
            </RosterTableProvider>
        </ClientIdentityProvider>
    )
}

export default RosterMappingCandidateList
